// After "Re-run" the mutation only queues the check. The new output and
// status land in the event a moment later. These helpers poll until that
// event actually changes so the open page can show it without a reload.

const DEFAULT_INTERVAL_MS = 1000;
const DEFAULT_TIMEOUT_MS = 90000;

export function eventResultSignature(event) {
  if (!event || !event.check) {
    return "";
  }
  const check = event.check;
  return [check.executed, check.status, check.output, event.timestamp].join(
    "\u0000",
  );
}

const executedAt = (event) => {
  const value = event && event.check && event.check.executed;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Prefer whichever copy of the event was produced by the later execution.
// When the timestamps tie, keep the explicitly refreshed copy.
export function preferFresherEvent(refreshed, queried) {
  if (!refreshed) {
    return queried || null;
  }
  if (!queried) {
    return refreshed;
  }
  const refreshedAt = executedAt(refreshed);
  const queriedAt = executedAt(queried);
  if (refreshedAt !== queriedAt) {
    return refreshedAt > queriedAt ? refreshed : queried;
  }
  const refreshedStamp = Number(refreshed.timestamp) || 0;
  const queriedStamp = Number(queried.timestamp) || 0;
  if (refreshedStamp !== queriedStamp) {
    return refreshedStamp > queriedStamp ? refreshed : queried;
  }
  return refreshed;
}

export function refreshTimeoutMs(event) {
  const timeout = event && event.check && Number(event.check.timeout);
  if (Number.isFinite(timeout) && timeout > 0) {
    return Math.min(timeout * 1000 + 20000, 180000);
  }
  return DEFAULT_TIMEOUT_MS;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// fetchEvent resolves to the latest event, or null when it is gone.
// Returns the first event whose signature differs from beforeSignature.
export async function waitForEventChange(fetchEvent, beforeSignature, options) {
  const intervalMs = (options && options.intervalMs) || DEFAULT_INTERVAL_MS;
  const timeoutMs = (options && options.timeoutMs) || DEFAULT_TIMEOUT_MS;
  const isCancelled = (options && options.isCancelled) || (() => false);
  const started = Date.now();

  while (!isCancelled() && Date.now() - started < timeoutMs) {
    await sleep(intervalMs);
    if (isCancelled()) {
      return null;
    }
    try {
      const event = await fetchEvent();
      if (event && eventResultSignature(event) !== beforeSignature) {
        return event;
      }
    } catch (err) {
      // A single failed read should not hide a result that arrives next.
    }
  }
  return null;
}
